import { useEffect, useState, useCallback } from 'react';
import { vibrate } from '@/lib/haptics';
import { safeSetItem } from '@/lib/safeStorage';

const PANIC_ENABLED_KEY = 'aphenis_panic_switch_enabled';
const PANIC_CODE_KEY = 'aphenis_panic_dismiss_code';
const DEFAULT_CODE = '1337';

export function isPanicSwitchEnabled(): boolean {
  try { return localStorage.getItem(PANIC_ENABLED_KEY) === 'true'; } catch { return false; }
}

export function setPanicSwitchEnabled(enabled: boolean): void {
  safeSetItem(PANIC_ENABLED_KEY, enabled ? 'true' : 'false');
}

export function getPanicDismissCode(): string {
  try { return localStorage.getItem(PANIC_CODE_KEY) || DEFAULT_CODE; } catch { return DEFAULT_CODE; }
}

export function setPanicDismissCode(code: string): void {
  safeSetItem(PANIC_CODE_KEY, code);
}

/**
 * Panic Quick-Switch: triple-tap the top of the screen to show decoy.
 * Dismiss by entering the secret code on the decoy calculator.
 */
export function usePanicSwitch(enabled: boolean) {
  const [showDecoy, setShowDecoy] = useState(false);

  const activateDecoy = useCallback(() => {
    setShowDecoy(true);
  }, []);

  const dismissDecoy = useCallback(() => {
    setShowDecoy(false);
  }, []);

  // Triple-tap detection at top 60px of screen
  useEffect(() => {
    if (!enabled) return;

    let tapCount = 0;
    let lastTap = 0;
    const TAP_TIMEOUT = 500;

    const handleTouch = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch || touch.clientY > 60) return; // Only top 60px

      const now = Date.now();
      if (now - lastTap < TAP_TIMEOUT) {
        tapCount++;
        if (tapCount >= 2) { // 3rd tap (0-indexed: 0, 1, 2)
          tapCount = 0;
          vibrate(15);
          activateDecoy();
        }
      } else {
        tapCount = 0;
      }
      lastTap = now;
    };

    window.addEventListener('touchstart', handleTouch, { passive: true });
    return () => window.removeEventListener('touchstart', handleTouch);
  }, [enabled, activateDecoy]);

  return { showDecoy, activateDecoy, dismissDecoy };
}
